import { useQueryRequest } from './query-request';
import {
  ItemWithId,
  PaginationState,
  QueryResponseContextProps,
  QueryState,
  Response,
  initialQueryResponse,
  initialQueryState
} from 'components/table/types';
import { QueryError } from 'features/shared/model';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UseQueryResult } from 'react-query';
import { WithChildren } from 'ts-types';
import { createResponseContext, stringifyRequestQuery } from 'utils/queries';

const crateQueryResponseContextFor = <T extends ItemWithId>() => createResponseContext<T>(initialQueryResponse);

type Props<T> = WithChildren & {
  queryResponseContext: React.Context<QueryResponseContextProps<T>>;
  runQuery: any;
};

const QueryResponseProvider = <T extends ItemWithId>({
  children,
  queryResponseContext: QueryResponseContext,
  runQuery
}: Props<T>) => {
  const { state } = useQueryRequest();
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state));
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state]);
  const { isFetching, refetch, data: response } = runQuery(state);

  // TODO check performance
  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery);
    }
  }, [updatedQuery]);

  return (
    <QueryResponseContext.Provider value={{ isLoading: isFetching, refetch, response, query }}>
      {children}
    </QueryResponseContext.Provider>
  );
};

const useQueryResponse = <T extends ItemWithId>(context: React.Context<QueryResponseContextProps<T>>) =>
  useContext(context);

const useQueryResponseCount = <T extends ItemWithId>(context: React.Context<QueryResponseContextProps<T>>) => {
  const { response } = useQueryResponse(context);
  if (!response) {
    return 0;
  }

  return response?.totalCount || 0;
};

const useQueryResponseData = <T extends ItemWithId>(context: React.Context<QueryResponseContextProps<T>>) => {
  const { response } = useQueryResponse(context);

  if (!response) {
    return [];
  }

  return response?.data || [];
};

const useQueryResponsePagination = <T extends ItemWithId>(context: React.Context<QueryResponseContextProps<T>>) => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState
  };

  const { response } = useQueryResponse(context);

  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState;
  }

  return response.payload.pagination;
};

const useQueryResponseLoading = <T extends ItemWithId>(
  context: React.Context<QueryResponseContextProps<T>>
): boolean => {
  const { isLoading } = useQueryResponse(context);
  return isLoading;
};

export {
  QueryResponseProvider,
  crateQueryResponseContextFor,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
  useQueryResponseCount
};
